import Phaser from "phaser";

/**
 * A class that wraps up our 2D platforming player logic. It creates, animates and moves a sprite in
 * response to WASD/arrow keys. Call its update method from the scene's update and call its destroy
 * method when you're done with the player.
 */
export default class Player {

  private scene!: Phaser.Scene
  private sprite!: Phaser.Types.Physics.Arcade.SpriteWithDynamicBody
  private keys!: any

  private coyoteDurationMs = 120
  private lastGroundedAt = 0
  private maxRunSpeed = 260
  private groundAcceleration = 2600
  private airAcceleration = 1800
  private groundDeceleration = 2200
  private airDeceleration = 1400
  private airTurnBoost = 1.35

  constructor(scene: Phaser.Scene, x: number, y: number) {
    this.scene = scene;

    // Create the animations we need from the player spritesheet
    const anims = scene.anims;
    anims.create({
      key: "player-idle",
      frames: anims.generateFrameNumbers("player", { start: 0, end: 3 }),
      frameRate: 3,
      repeat: -1
    });
    anims.create({
      key: "player-run",
      frames: anims.generateFrameNumbers("player", { start: 8, end: 15 }),
      frameRate: 12,
      repeat: -1
    });

    // Create the physics-based sprite that we will move around and animate
    this.sprite = scene.physics.add
      .sprite(x, y, "player", 0)
      .setDrag(1000, 0)
      .setMaxVelocity(300, 400)
      .setSize(18, 24)
      .setOffset(7, 8);;

    if (!scene.input.keyboard) {
      throw new Error("Keyboard input is not available for Player")
    }

    // Track the arrow keys, WASD, and spacebar
    const { LEFT, RIGHT, UP, SPACE, W, A, D } = Phaser.Input.Keyboard.KeyCodes;
    this.keys = scene.input.keyboard.addKeys({
      left: LEFT,
      right: RIGHT,
      up: UP,
      space: SPACE,
      w: W,
      a: A,
      d: D
    });
  }

  update() {
    const keys = this.keys;
    const sprite = this.sprite;
    const now = this.scene.time.now;
    const dt = this.scene.game.loop.delta / 1000;
    const onGround = sprite.body.blocked.down;
    const inputX = (keys.left.isDown || keys.a.isDown) ? -1 : (keys.right.isDown || keys.d.isDown) ? 1 : 0;

    if (onGround) {
      this.lastGroundedAt = now;
    }

    // Manually steer horizontal velocity for tighter control, especially in the air.
    if (inputX !== 0) {
      const acceleratingOpposite = Math.sign(sprite.body.velocity.x) !== 0 && Math.sign(sprite.body.velocity.x) !== inputX;
      const baseAcceleration = onGround ? this.groundAcceleration : this.airAcceleration;
      const acceleration = acceleratingOpposite && !onGround
        ? baseAcceleration * this.airTurnBoost
        : baseAcceleration;

      const vx = Phaser.Math.Clamp(
        sprite.body.velocity.x + inputX * acceleration * dt,
        -this.maxRunSpeed,
        this.maxRunSpeed
      );

      sprite.setVelocityX(vx);
      sprite.setAccelerationX(0);
    } else {
      const deceleration = onGround ? this.groundDeceleration : this.airDeceleration;
      const velocityX = sprite.body.velocity.x;
      const step = deceleration * dt;
      const nextVelocityX = Math.abs(velocityX) <= step
        ? 0
        : velocityX - Math.sign(velocityX) * step;

      sprite.setVelocityX(nextVelocityX);
      sprite.setAccelerationX(0);
    }

    if (inputX < 0) {
      sprite.setFlipX(true);
    } else if (inputX > 0) {
      sprite.setFlipX(false);
    }

    const jumpPressed =
      Phaser.Input.Keyboard.JustDown(keys.up) ||
      Phaser.Input.Keyboard.JustDown(keys.w) ||
      Phaser.Input.Keyboard.JustDown(keys.space);

    const canCoyoteJump = now - this.lastGroundedAt <= this.coyoteDurationMs;

    // Allow jump while grounded or briefly after leaving a platform
    if (jumpPressed && (onGround || canCoyoteJump)) {
      sprite.setVelocityY(-500);
      this.lastGroundedAt = -Infinity; // consume coyote jump
    }

    // Update the animation/texture based on the state of the player
    if (onGround) {
      if (sprite.body.velocity.x !== 0) sprite.anims.play("player-run", true);
      else sprite.anims.play("player-idle", true);
    } else {
      sprite.anims.stop();
      sprite.setTexture("player", 10);
    }
  }

  destroy() {
    this.sprite.destroy();
  }

  getSprite() {
    return this.sprite;
  }

  freeze() {
    this.sprite.body.moves = false;
  }


}